package puzzlekey

/** AES-256-GCM with a 12-byte IV and a 128-bit tag appended to the ciphertext. */
interface Aead {
    suspend fun encrypt(key: ByteArray, iv: ByteArray, plaintext: ByteArray): ByteArray

    /** @throws AeadException when the key, IV, ciphertext or tag don't authenticate. */
    suspend fun decrypt(key: ByteArray, iv: ByteArray, ciphertext: ByteArray): ByteArray
}

class AeadException(cause: Throwable) : Exception("AES-GCM authentication failed", cause)

/**
 * Key derivation. HKDF-SHA256 turns high-entropy input into wrapping keys (ADR-0006);
 * PBKDF2-SHA256 is the slow KDF for low-entropy input such as Puzzle bits.
 */
interface Kdf {
    suspend fun hkdf(ikm: ByteArray, salt: ByteArray, info: ByteArray, length: Int): ByteArray

    suspend fun pbkdf2(password: ByteArray, salt: ByteArray, iterations: Int, length: Int): ByteArray
}

/** The platform's crypto: `javax.crypto` on the JVM, WebCrypto in the browser and node. */
expect object Crypto : Aead, Kdf {
    // Restated so the common metadata compilation (`./gradlew build`) sees them as implemented.
    override suspend fun encrypt(key: ByteArray, iv: ByteArray, plaintext: ByteArray): ByteArray
    override suspend fun decrypt(key: ByteArray, iv: ByteArray, ciphertext: ByteArray): ByteArray
    override suspend fun hkdf(ikm: ByteArray, salt: ByteArray, info: ByteArray, length: Int): ByteArray
    override suspend fun pbkdf2(password: ByteArray, salt: ByteArray, iterations: Int, length: Int): ByteArray
}
